import type { PropsWithChildren } from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { $enum } from "ts-enum-util";

import { Circle, cn, type CircleBadgeProps, type CircleProps } from "@eisox/design-system";
import { colors } from "@eisox/tokens";

import { polymorphicForwardRef } from "~/types/polymorphic-forward-ref";

const backgroundColorToTailwind: Record<keyof typeof colors.primary, string> = {
  white: "bg-primary-white after:border-t-primary-white",
  gray: "bg-primary-gray after:border-t-primary-gray",
  darkerGray: "bg-primary-darker-gray after:border-t-primary-darker-gray",
  darkGray: "bg-primary-dark-gray after:border-t-primary-dark-gray",
  blue: "bg-primary-blue after:border-t-primary-blue",
  darkBlue: "bg-primary-dark-blue after:border-t-primary-dark-blue",
  green: "bg-primary-green after:border-t-primary-green",
  red: "bg-primary-red after:border-t-primary-red",
  orange: "bg-primary-orange after:border-t-primary-orange",
  warning: "bg-primary-warning after:border-t-primary-warning",
};

const expandedItemVariants = cva(
  "pointer-events-auto relative flex w-max min-w-[25px] items-center justify-center gap-x-[5px] rounded-[5px] px-[7px] py-[5px] text-sm text-primary-white opacity-50 after:absolute after:bottom-[-4px] after:left-1/2 after:h-[5px] after:w-[10px] after:-translate-x-1/2 after:bg-inherit after:content-[''] after:[clip-path:polygon(0%_0%,100%_0%,50%_100%)] data-[disabled=true]:bg-primary-dark-gray data-[disabled=true]:opacity-100 data-[selected=true]:opacity-100",
  {
    variants: {
      backgroundColor: $enum(colors.primary)
        .getKeys()
        .reduce(
          (acc, key) => {
            acc[key] = backgroundColorToTailwind[key];
            return acc;
          },
          {} as Record<keyof typeof colors.primary, string>,
        ),
    },
  },
);

const ExpandedItemDefaultAsType = "div";

interface ExpandedItemProps extends VariantProps<typeof expandedItemVariants>, PropsWithChildren {
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  iconProps?: Pick<CircleProps, "backgroundColor" | "color">;
  icon?: React.ReactNode;
  error?: CircleBadgeProps["variant"];
}

const ExpandedItem = polymorphicForwardRef<typeof ExpandedItemDefaultAsType, ExpandedItemProps>(
  (
    {
      as: Element = ExpandedItemDefaultAsType,
      selected = true,
      disabled = false,
      error,
      iconProps = {
        backgroundColor: "white",
        color: "darkBlue",
      },
      icon,
      backgroundColor = "blue",
      className,
      children,
      ...props
    },
    forwardedRef,
  ) => (
    <Element
      {...props}
      ref={forwardedRef}
      className={cn(
        expandedItemVariants({ backgroundColor }),
        error &&
          "before:absolute before:top-[-3px] before:right-[-8px] before:h-[10px] before:w-[10px] before:-translate-x-1/2 before:rounded-full before:border before:border-primary-white before:bg-primary-red before:content-['']",
        error === "warning" && "before:bg-primary-orange",
        className,
      )}
      data-selected={selected}
      data-disabled={disabled}
    >
      {icon && (
        <Circle.Root
          size={20}
          color={iconProps.color ?? backgroundColor ?? undefined}
          backgroundColor={iconProps.backgroundColor ?? undefined}
        >
          {icon}
          {error && <Circle.Badge variant={error} />}
        </Circle.Root>
      )}
      {children ?? "--"}
    </Element>
  ),
);

export default ExpandedItem;
export { ExpandedItem };
export type { ExpandedItemProps };
